import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { ImmediateStateMatcher } from '@common';
import { InstallationFeaturesStore } from '@store';

import { EditFeatureValueData } from '../../models/editFeatureValueData.model';
import { Decimal } from 'decimal.js';

@Component({
  selector: 'app-edit-feature-value-modal',
  templateUrl: './edit-feature-value-modal.component.html',
  styleUrls: ['./edit-feature-value-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditFeatureValueModalComponent implements OnInit {
  featureValueControl: FormControl<string | number>;
  validators: ValidatorFn[] = [];
  matcher = new ImmediateStateMatcher();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: EditFeatureValueData,
    private dialogRef: MatDialogRef<EditFeatureValueModalComponent>,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.initFormValidators();
    this.featureValueControl = new FormControl<string | number>(this.data.value, this.validators);
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onClear(): void {
    this.featureValueControl.setValue('');
  }

  isFeatureValueChanged(): boolean {
    return this.featureValueControl.value !== this.data.value;
  }

  onSaveFeatureValue(): void {
    const value =
      this.data.inputSettings.type === 'number'
        ? Number(this.featureValueControl.value)
        : this.featureValueControl.value;

    if (this.featureValueControl.valid && this.isFeatureValueChanged()) {
      this.installationFeaturesStore.updateFeature(
        this.data.onSaveProperties.featureName,
        value,
        this.data.onSaveProperties.propertyName,
        this.data.onSaveProperties.paramName,
        this.data.onSaveProperties.commandName
      );
    }
    this.onClose();
  }

  private initFormValidators(): void {
    if (this.data.inputSettings.isRequired) {
      this.validators.push(Validators.required);
    }
    if (this.data.inputSettings.min) {
      this.validators.push(Validators.min(this.data.inputSettings.min));
    }
    if (this.data.inputSettings.max) {
      this.validators.push(Validators.max(this.data.inputSettings.max));
    }
    if (this.data.inputSettings.pattern) {
      this.validators.push(Validators.pattern(this.data.inputSettings.pattern));
    }
    if (this.data.inputSettings.step) {
      this.validators.push(this.steppingValidator(this.data.inputSettings.step));
    }
  }

  private steppingValidator(step: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return this.isSteppingCorrect(step, control.value) ? null : { step: true };
    };
  }

  // Check if input value is a multiple of the step value
  private isSteppingCorrect(stepping: number, value: number | string): boolean {
    // decimal.js requries string usage
    const a = new Decimal((!Number.isNaN(+value) && value?.toString()) || 0);
    const b = new Decimal(stepping.toString());
    const result = a.div(b).toString();

    return Number.isInteger(+result.toString());
  }
}
